import {MouseEvent, ReactElement, useState} from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion as MuiAccordion,
  AccordionSummary as MuiAccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  IconButton,
  AccordionSummaryProps,
  styled,
  AccordionProps,
  Tooltip
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {Section as ISection} from 'interfaces/Project';
import {System} from 'interfaces/System';
import getSectionName from 'utils/getSectionName';
import {UseConfiguratorActions} from 'views/Calculator/hooks/useConfigurator';
import Section from './Section';

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary {...props} />
))(({theme}) => ({
  backgroundColor: theme.palette.grey[100],
  minHeight: 64,
  '& .MuiAccordionSummary-content': {
    margin: theme.spacing(1.5, 0)
  },
  '& .section-chevron': {
    transition: 'transform 0.2s'
  },
  '&.Mui-expanded .section-chevron': {
    transform: 'rotate(180deg)'
  }
}));

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
  borderRadius: '18px',
  overflow: 'hidden',
  '&:before': {
    display: 'none'
  }
}));

type Props = {
  section: ISection;
  configuratorActions: UseConfiguratorActions;
  index: number;
  systems: System[];
  otherSections: ISection[];
  onSaveProject: (onSuccess?: () => void) => void;
};

function SectionAccordion({
  section,
  configuratorActions,
  systems,
  otherSections,
  index,
  onSaveProject
}: Props): ReactElement {
  const {t} = useTranslation(['common']);
  const [expanded, setExpanded] = useState(false);

  function handleDelete(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    configuratorActions.deleteSection(section.id);
  }

  function handleSaveSection() {
    onSaveProject(() => setExpanded(false));
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={(_event, isExpanded) => setExpanded(isExpanded)}
    >
      <AccordionSummary
        aria-controls={`section-${section.id}-content`}
        id={`section-${section.id}-header`}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minWidth: '100%'
          }}
        >
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <Typography sx={{fontSize: 28, fontWeight: 400, lineHeight: 1.334, color: 'ctaBlack'}}>
              {getSectionName(section, index + 1, t)}
            </Typography>
            <ExpandMoreIcon className="section-chevron" sx={{color: 'text.primary'}} />
          </Box>
          <Tooltip title={t('common:deleteSection')}>
            <IconButton size="small" aria-label="remove" onClick={handleDelete} sx={{color: 'ctaDark'}}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{p: {xs: 2, md: 3}, bgcolor: 'sectionBackground'}}>
        <Section
          section={section}
          systems={systems}
          configuratorActions={configuratorActions}
          otherSections={otherSections}
          onSaveProject={handleSaveSection}
        />
      </AccordionDetails>
    </Accordion>
  );
}

export default SectionAccordion;
